"use client";

import { c as _c } from "react/compiler-runtime";
import { calculateDistance } from "../../utils";
import { APIProvider, InfoWindow, Map, Marker, useMap } from "@vis.gl/react-google-maps";
import { useCallback, useEffect, useState } from "react";
import { FaClinicMedical, FaCompress, FaCrosshairs, FaLocationArrow } from "react-icons/fa";
import { Button } from "@/components/ui";
import { useSelfScheduleContext } from "@/contexts/self-schedule-provider";
import { formatAddress } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { useToast } from "@/lib/use-toast";
import { PublicLocationDetail } from "@/types/location";
interface LocationsMapProps {
  locations: PublicLocationDetail[];
  className?: string;
  onLocationSelect?: (locationId: string) => void;
}

// Inner component that has access to the map via useMap hook
const MapContent = ({
  locationsWithCoordinates,
  onLocationSelect,
  setMapOpen,
  mapOpen
}: {
  locations: PublicLocationDetail[];
  locationsWithCoordinates: PublicLocationDetail[];
  onLocationSelect?: (locationId: string) => void;
  setMapOpen: React.Dispatch<React.SetStateAction<boolean>>;
  mapOpen: boolean;
}) => {
  const map = useMap();
  const {
    userLocation,
    requestUserLocation
  } = useSelfScheduleContext();
  const [selectedMarker, setSelectedMarker] = useState<string | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const {
    toast
  } = useToast();
  const handleMarkerClick = useCallback((locationId: string) => {
    setSelectedMarker(selectedMarker === locationId ? null : locationId);
  }, [selectedMarker]);
  const handleInfoWindowClose = useCallback(() => {
    setSelectedMarker(null);
  }, []);
  const handleLocationRequest = useCallback(async () => {
    setIsLoadingLocation(true);
    try {
      await requestUserLocation();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to get location",
        variant: "error"
      });
    } finally {
      setIsLoadingLocation(false);
    }
  }, [requestUserLocation]);

  // Auto-request location when component mounts
  useEffect(() => {
    if (!userLocation) {
      handleLocationRequest();
    }
  }, []);
  const handleLocationButtonClick = useCallback(() => {
    if (userLocation && map) {
      // If we have user location and map is available, center on it
      map.panTo(userLocation);
    } else {
      // If no location or map not available, request location
      handleLocationRequest();
    }
  }, [userLocation, map, handleLocationRequest]);
  const findClosestClinic = useCallback(() => {
    if (!userLocation || !map || locationsWithCoordinates.length === 0) {
      return;
    }
    let closestClinic = locationsWithCoordinates[0];
    let shortestDistance = Infinity;
    locationsWithCoordinates.forEach(clinic => {
      const clinicLat = clinic.latitude!;
      const clinicLng = clinic.longitude!;
      const userLat = userLocation.lat;
      const userLng = userLocation.lng;
      const distance = calculateDistance(userLat, userLng, clinicLat, clinicLng);
      if (distance < shortestDistance) {
        shortestDistance = distance;
        closestClinic = clinic;
      }
    });

    // Center map on closest clinic
    map.panTo({
      lat: closestClinic.latitude!,
      lng: closestClinic.longitude!
    });
  }, [userLocation, map, locationsWithCoordinates]);
  return <>
      {/* User Location Marker */}
      {userLocation && <Marker position={userLocation} title="Your location" icon={{
      fillColor: "green",
      fillOpacity: 1,
      path: "M125 56h-7.591C113.931 32.55 95.41875 14.069 72 10.591V3c0-1.65675-1.34325-3-3-3h-10c-1.65675 0-3 1.34325-3 3v7.591C32.55 14.069 14.069 32.55 10.591 56H3c-1.65675 0-3 1.34325-3 3v10c0 1.65675 1.34325 3 3 3h7.591C14.069 95.41875 32.55 113.931 56 117.409V125c0 1.65675 1.34325 3 3 3h10c1.65675 0 3-1.34325 3-3v-7.591C95.41875 113.931 113.931 95.41875 117.409 72H125c1.65675 0 3-1.34325 3-3v-10c0-1.65675-1.34325-3-3-3zM72 101.1585V91c0-1.65675-1.34325-3-3-3h-10c-1.65675 0-3 1.34325-3 3v10.1585C41.4565 98.058 29.94575 86.56075 26.8415 72H37c1.65675 0 3-1.34325 3-3v-10c0-1.65675-1.34325-3-3-3H26.6585C29.942 41.4565 41.43925 29.94575 56 26.8415V37c0 1.65675 1.34325 3 3 3h10c1.65675 0 3-1.34325 3-3V26.6585C86.5435 29.942 98.05425 41.43925 101.1585 56H91c-1.65675 0-3 1.34325-3 3v10c0 1.65675 1.34325 3 3 3h10.1585C98.058 86.5435 86.56075 98.05425 72 101.1585zM72 64c0 4.41825-3.58175 8-8 8s-8-3.58175-8-8c0-4.41825 3.58175-8 8-8s8 3.58175 8 8z",
      scale: 0.3
    }} />}

      {/* Location Markers */}
      {locationsWithCoordinates.map(location => <Marker key={location.id} position={{
      lat: location.latitude!,
      lng: location.longitude!
    }} onClick={() => handleMarkerClick(location.id)} />)}

      {selectedMarker && <InfoWindow position={{
      lat: locationsWithCoordinates.find(loc => loc.id === selectedMarker)!.latitude!,
      lng: locationsWithCoordinates.find(loc_0 => loc_0.id === selectedMarker)!.longitude!
    }} onClose={handleInfoWindowClose}>
          <>
            <div className="max-w-48 p-1 pt-0">
              {(() => {
            const location_0 = locationsWithCoordinates.find(loc_1 => loc_1.id === selectedMarker);
            if (!location_0) return null;
            return <div className="space-y-2">
                    {location_0.photoUrl && <img src={location_0.photoUrl} alt={location_0.name} className="aspect-video w-full rounded-lg object-cover" />}
                    <div>
                      <h4 className="text-body-strong">{location_0.name}</h4>
                      <div className="[&>p]:text-caption [&>p]:text-muted-foreground">
                        {!location_0.isVirtual && formatAddress(location_0.addressLineOne, location_0.city, location_0.state, location_0.zipCode, location_0.addressLineTwo)}
                      </div>
                    </div>

                    {location_0.description && <p className="text-caption text-foreground-secondary">
                        {location_0.description}
                      </p>}

                    {location_0.phoneNumber && <div className="text-caption">
                        <span className="font-medium">Phone: </span>
                        <span className="text-foreground-secondary">
                          {location_0.phoneNumber}
                        </span>
                      </div>}

                    <div className="h-6 w-full " />

                    {onLocationSelect && <div className="absolute bottom-2 left-1/2 flex w-full -translate-x-1/2 justify-center  pt-2 ">
                        <Button variant="primary" size="sm" onClick={() => onLocationSelect(location_0.id)} className="w-2/3 shadow-sm">
                          Select Location
                        </Button>
                      </div>}
                  </div>;
          })()}
            </div>
          </>
        </InfoWindow>}

      {/* Location Request Button */}
      <div className="absolute right-2 top-2 z-10 flex gap-2">
        {mapOpen && <button onClick={e => {
        e.stopPropagation();
        e.preventDefault();
        setMapOpen(false);
      }} className="flex items-center gap-2 rounded-lg bg-background/80 px-3 py-2 text-body-strong shadow-lg backdrop-blur-sm transition-all hover:scale-105 hover:shadow-xl md:hidden" title="Collapse Map">
            <FaCompress className="h-4 w-4 text-primary" />
            <span className="hidden md:inline">Collapse Map</span>
          </button>}
        <button onClick={handleLocationButtonClick} disabled={isLoadingLocation} className={cx("flex items-center gap-2 rounded-lg bg-background/80 px-3 py-2 text-body-strong shadow-lg backdrop-blur-sm transition-all", isLoadingLocation ? "cursor-not-allowed opacity-50" : "hover:scale-105 hover:shadow-xl")} title={userLocation ? "Center on my location" : "Find my location"}>
          {isLoadingLocation ? <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-primary" /> : userLocation ? <FaLocationArrow className="h-4 w-4 text-status-success-text" /> : <FaCrosshairs className="h-4 w-4 text-foreground-secondary" />}
          <span className="hidden md:inline">
            {isLoadingLocation ? "Finding..." : userLocation ? "My Location" : "Find Location"}
          </span>
        </button>

        {userLocation && <button onClick={findClosestClinic} className="flex items-center gap-2 rounded-lg bg-background/80 px-3 py-2 text-body-strong shadow-lg backdrop-blur-sm transition-all hover:scale-105 hover:shadow-xl" title="Center on closest clinic">
            <FaClinicMedical className="h-4 w-4 text-primary" />
            <span className="hidden md:inline">Closest Clinic</span>
          </button>}
      </div>
    </>;
};
const LocationsMap = t0 => {
  const $ = _c(26);
  const {
    locations,
    className,
    onLocationSelect
  } = t0;
  const {
    userLocation
  } = useSelfScheduleContext();
  const [mapOpen, setMapOpen] = useState(false);
  let locationsWithCoordinates;
  let t1;
  let t2;
  if ($[0] !== locations || $[1] !== userLocation) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      locationsWithCoordinates = locations.filter(_temp);
      const getMapBounds = () => {
        const allPoints = [...locationsWithCoordinates];
        if (userLocation && locationsWithCoordinates.length > 0) {
          const firstLocation = locationsWithCoordinates[0];
          const userLat = userLocation.lat;
          const userLng = userLocation.lng;
          const locationLat = firstLocation.latitude;
          const locationLng = firstLocation.longitude;
          const distance = calculateDistance(userLat, userLng, locationLat, locationLng);
          if (distance <= 50) {
            allPoints.push({
              id: "user-location",
              latitude: userLocation.lat,
              longitude: userLocation.lng
            } as any);
          }
        }
        if (allPoints.length === 0) {
          return null;
        }
        const lats = allPoints.map(_temp2).filter(_temp3);
        const lngs = allPoints.map(_temp4).filter(_temp5);
        if (lats.length === 0 || lngs.length === 0) {
          return null;
        }
        return {
          north: Math.max(...lats),
          east: Math.max(...lngs),
          south: Math.min(...lats),
          west: Math.min(...lngs),
          padding: 100
        };
      };
      if (locationsWithCoordinates.length === 0) {
        t2 = null;
        break bb0;
      }
      t1 = getMapBounds();
    }
    $[0] = locations;
    $[1] = userLocation;
    $[2] = locationsWithCoordinates;
    $[3] = t1;
    $[4] = t2;
  } else {
    locationsWithCoordinates = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  const mapBounds = t1;
  const t3 = mapOpen ? "max-h-96" : "max-h-24";
  let t4;
  if ($[5] !== className || $[6] !== t3) {
    t4 = cx("relative h-96 overflow-hidden", "w-full rounded-xl transition-all ease-in-out", t3, className);
    $[5] = className;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  let t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setMapOpen(true);
    t6 = () => setMapOpen(false);
    t7 = () => setMapOpen(true);
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t5 = $[8];
    t6 = $[9];
    t7 = $[10];
  }
  let t8;
  if ($[11] !== locationsWithCoordinates || $[12] !== mapBounds) {
    t8 = mapBounds || {
      north: locationsWithCoordinates[0].latitude,
      east: locationsWithCoordinates[0].longitude,
      south: locationsWithCoordinates[0].latitude,
      west: locationsWithCoordinates[0].longitude,
      padding: 100
    };
    $[11] = locationsWithCoordinates;
    $[12] = mapBounds;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== locations || $[15] !== locationsWithCoordinates || $[16] !== mapOpen || $[17] !== onLocationSelect) {
    t9 = <MapContent locations={locations} locationsWithCoordinates={locationsWithCoordinates} onLocationSelect={onLocationSelect} setMapOpen={setMapOpen} mapOpen={mapOpen} />;
    $[14] = locations;
    $[15] = locationsWithCoordinates;
    $[16] = mapOpen;
    $[17] = onLocationSelect;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== className || $[20] !== t8 || $[21] !== t9) {
    t10 = <APIProvider apiKey="AIzaSyAcJo2m5es8ZbUn3T9lsL4H--1rfd7tJRM"><Map defaultBounds={t8} gestureHandling="cooperative" disableDefaultUI={true} className={className}>{t9}</Map></APIProvider>;
    $[19] = className;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t4) {
    t11 = <div className={t4} onMouseEnter={t5} onMouseLeave={t6} onTouchStart={t7}>{t10}</div>;
    $[23] = t10;
    $[24] = t4;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
};
export default LocationsMap;
function _temp(location) {
  if (location.isVirtual) {
    return false;
  }
  if (!location.latitude || !location.longitude) {
    return false;
  }
  return true;
}
function _temp2(point) {
  return point.latitude;
}
function _temp3(lat) {
  return lat !== undefined;
}
function _temp4(point_0) {
  return point_0.longitude;
}
function _temp5(lng) {
  return lng !== undefined;
}